import { useEffect } from 'react';
import { toast } from '@/shared/ui/sonner';
import { getOAuthProvider, providerForReturnParams } from './providerRegistry';

// Full-redirect fallback path only: when a popup is blocked the OAuth callback
// lands the top-level tab on `?<provider>=connected|error` (see useConnectProvider).
// The popup path is handled earlier by postOAuthReturnFromPopup in the bootstrap
// and never reaches here. Surface a toast and strip the param so a refresh
// doesn't re-toast. Mounted once, app-wide, in AppShell.
export function useOAuthReturnHandler(enabled = true): void {
  useEffect(() => {
    if (!enabled || window.opener) return;

    let match: ReturnType<typeof providerForReturnParams>;
    try {
      match = providerForReturnParams(new URLSearchParams(window.location.search));
    } catch {
      return;
    }
    if (!match) return;

    const label = getOAuthProvider(match.provider.id)?.label ?? match.provider.id;
    if (match.outcome === 'connected') {
      toast.success(`${label} connected`, { duration: 4000 });
    } else {
      toast.error(`Could not connect ${label}. Please try again.`, { duration: 4000 });
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(match.provider.returnParam);
      window.history.replaceState(null, '', url.toString());
    } catch {
      // ignore URL cleanup failures — the toast already fired.
    }
  }, [enabled]);
}
